import React, { useState, useEffect } from 'react';
import Header from '../../components/common/Header/Header';
import Footer from '../../components/common/Footer/Footer';
import Layout from '../../components/common/Layout/Layout';
import { ArrowRight, Clock, User, TrendingUp, DollarSign, PieChart, BarChart3, Target, Shield } from 'lucide-react';

const BlogPage = () => {
    const [selectedBlog, setSelectedBlog] = useState(null);
    const [blogPosts, setBlogPosts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Function to clean blog content and remove size buttons
    const cleanBlogContent = (content) => {
        if (!content) return '';
        
        // Create a temporary div to parse HTML
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = content;
        
        // Remove all buttons with text Small, Medium, Large, Full
        const buttons = tempDiv.querySelectorAll('button');
        buttons.forEach(button => {
            const text = button.textContent.trim();
            if (text === 'Small' || text === 'Medium' || text === 'Large' || text === 'Full') {
                button.remove();
            }
        });
        
        // Also remove any divs that only contained these buttons
        const divs = tempDiv.querySelectorAll('div');
        divs.forEach(div => {
            if (div.children.length === 0 && !div.textContent.trim()) {
                div.remove();
            }
        });
        
        return tempDiv.innerHTML;
    };

    // API configuration
    const API_BASE_URL = `${process.env.REACT_APP_API_BASE_URL}/api/blogs`;
    
    // Add search and filter states
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedCategory, setSelectedCategory] = useState('');
    const [categories, setCategories] = useState([]);
    const [filteredBlogs, setFilteredBlogs] = useState([]);

    // Icon mapping for categories
    const categoryIcons = {
        'FUNDAMENTALS': <TrendingUp size={40} />,
        'STRATEGY': <BarChart3 size={40} />,
        'TAX PLANNING': <Shield size={40} />,
        'EQUITY FUNDS': <TrendingUp size={40} />,
        'DEBT FUNDS': <Shield size={40} />,
        'FINANCIAL PLANNING': <Target size={40} />,
        'DEFAULT': <TrendingUp size={40} />
    };

    // Default gradient colors for categories
    const categoryColors = {
        'FUNDAMENTALS': "linear-gradient(135deg, #1a2332 0%, #2a4158 100%)",
        'STRATEGY': "linear-gradient(135deg, #2a4158 0%, #1a2332 100%)",
        'TAX PLANNING': "linear-gradient(135deg, #3182ce 0%, #2c5aa0 100%)",
        'EQUITY FUNDS': "linear-gradient(135deg, #4a5568 0%, #2d3748 100%)",
        'DEBT FUNDS': "linear-gradient(135deg, #718096 0%, #4a5568 100%)",
        'FINANCIAL PLANNING': "linear-gradient(135deg, #1a202c 0%, #2d3748 100%)",
        'DEFAULT': "linear-gradient(135deg, #1a2332 0%, #2a4158 100%)"
    };

    // Fetch blogs from backend
    const fetchBlogs = async () => {
        try {
            setLoading(true);
            setError(null);
            
            const response = await fetch(`${API_BASE_URL}/public`, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Key': process.env.REACT_APP_WINNEST_API_KEY
                },
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();
            
            // Transform backend data to match frontend format
            const transformedBlogs = data.map(blog => ({
                id: blog.id,
                title: blog.title || 'Untitled Blog',
                excerpt: blog.excerpt || 'No excerpt available...',
                author: blog.author || 'Unknown Author',
                readTime: blog.readTime || '5 MIN READ',
                category: blog.category || 'GENERAL',
                bgColor: blog.bgColor || categoryColors[blog.category] || categoryColors['DEFAULT'],
                icon: categoryIcons[blog.category] || categoryIcons['DEFAULT'],
                content: blog.content || '<p>No content available.</p>',
                slug: blog.slug,
                featuredImageUrl: blog.featuredImageUrl,
                tags: blog.tags || [],
                createdAt: blog.createdAt,
                updatedAt: blog.updatedAt
            }));

            setBlogPosts(transformedBlogs);
            setFilteredBlogs(transformedBlogs);
        } catch (error) {
            console.error('Error fetching blogs:', error);
            setError('Failed to load blogs. Please try again later.');
            
            // No fallback data - only show database content or error
            setBlogPosts([]);
            setFilteredBlogs([]);
        } finally {
            setLoading(false);
        }
    };

    // Fetch categories
    const fetchCategories = async () => {
        try {
            const response = await fetch(`${API_BASE_URL}/public/categories`, {
            headers: {
                'Content-Type': 'application/json',
                'X-API-Key': process.env.REACT_APP_WINNEST_API_KEY
            }
        });
            if (response.ok) {
                const data = await response.json();
                setCategories(data);
            }
        } catch (error) {
            console.error('Error fetching categories:', error);
        }
    };

    // Filter blogs based on search and category
    const filterBlogs = () => {
        let filtered = blogPosts;

        // Filter by search term
        if (searchTerm) {
            filtered = filtered.filter(blog => 
                blog.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                blog.excerpt.toLowerCase().includes(searchTerm.toLowerCase()) ||
                blog.author.toLowerCase().includes(searchTerm.toLowerCase())
            );
        }

        // Filter by category
        if (selectedCategory) {
            filtered = filtered.filter(blog => blog.category === selectedCategory);
        }

        setFilteredBlogs(filtered);
    };

    // Update filtered blogs when search or category changes
    useEffect(() => {
        filterBlogs();
    }, [searchTerm, selectedCategory, blogPosts]);

    // Fetch blog by slug if provided in URL
    const fetchBlogBySlug = async (slug) => {
        try {
            const response = await fetch(`${API_BASE_URL}/public/slug/${slug}`, {
                headers: {
                    'Content-Type': 'application/json',
                    'X-API-Key': process.env.REACT_APP_WINNEST_API_KEY
                }
            });
            if (response.ok) {
                const blog = await response.json();
                const transformedBlog = {
                    id: blog.id,
                    title: blog.title || 'Untitled Blog',
                    excerpt: blog.excerpt || 'No excerpt available...',
                    author: blog.author || 'Unknown Author',
                    readTime: blog.readTime || '5 MIN READ',
                    category: blog.category || 'GENERAL',
                    bgColor: blog.bgColor || categoryColors[blog.category] || categoryColors['DEFAULT'],
                    icon: categoryIcons[blog.category] || categoryIcons['DEFAULT'],
                    content: blog.content || '<p>No content available.</p>',
                    slug: blog.slug,
                    featuredImageUrl: blog.featuredImageUrl,
                    tags: blog.tags || [],
                    createdAt: blog.createdAt,
                    updatedAt: blog.updatedAt
                };
                return transformedBlog;
            }
        } catch (error) {
            console.error('Error fetching blog by slug:', error);
        }
        return null;
    };

    // Default blog data removed - only database data allowed
    /* Commented out - no fallback data allowed
    const getDefaultBlogs = () => [
        {
            id: 1,
            title: "MUTUAL FUND BASICS: A BEGINNER'S GUIDE TO WEALTH BUILDING",
            excerpt: "Learn the fundamentals of mutual fund investing and how to start your wealth-building journey with smart investment strategies...",
            author: "RAJESH SHARMA",
            readTime: "5 MIN READ",
            category: "FUNDAMENTALS",
            bgColor: "linear-gradient(135deg, #1a2332 0%, #2a4158 100%)",
            icon: <TrendingUp size={40} />,
            content: `
                <h2>Understanding Mutual Funds</h2>
                <p>Mutual funds are investment vehicles that pool money from multiple investors to purchase securities like stocks, bonds, and other financial instruments. This pooling allows individual investors to diversify their portfolios without needing large amounts of capital.</p>
                
                <h3>Key Benefits of Mutual Fund Investing:</h3>
                <ul>
                    <li><strong>Diversification:</strong> Spread risk across multiple securities</li>
                    <li><strong>Professional Management:</strong> Expert fund managers handle investment decisions</li>
                    <li><strong>Liquidity:</strong> Easy to buy and sell fund units</li>
                    <li><strong>Affordability:</strong> Start investing with small amounts</li>
                    <li><strong>Transparency:</strong> Regular reporting and disclosure</li>
                </ul>
                
                <h3>Getting Started</h3>
                <p>Before investing in mutual funds, consider your financial goals, risk tolerance, and investment timeline. Start with SIP (Systematic Investment Plan) to develop a disciplined investment habit.</p>
                
                <h3>Types of Mutual Funds</h3>
                <p>From equity funds for growth to debt funds for stability, choose funds that align with your investment objectives and risk profile.</p>
            `
        },
        {
            id: 2,
            title: "SIP VS LUMP SUM: WHICH INVESTMENT STRATEGY WORKS BETTER?",
            excerpt: "Compare systematic investment plans with lump sum investments to understand which approach suits your financial goals and market conditions...",
            author: "PRIYA PATEL",
            readTime: "4 MIN READ",
            category: "STRATEGY",
            bgColor: "linear-gradient(135deg, #2a4158 0%, #1a2332 100%)",
            icon: <BarChart3 size={40} />,
            content: `
                <h2>SIP vs Lump Sum Investment</h2>
                <p>One of the most common dilemmas investors face is whether to invest through SIP (Systematic Investment Plan) or make a lump sum investment. Both strategies have their merits depending on market conditions and personal circumstances.</p>
                
                <h3>SIP (Systematic Investment Plan)</h3>
                <p>SIP involves investing a fixed amount regularly (monthly/quarterly) regardless of market conditions.</p>
                
                <h4>Advantages of SIP:</h4>
                <ul>
                    <li>Rupee cost averaging reduces impact of market volatility</li>
                    <li>Disciplined investing habit formation</li>
                    <li>Lower entry barrier - start with as little as ₹500</li>
                    <li>Reduces timing risk</li>
                    <li>Power of compounding over time</li>
                </ul>
                
                <h3>Lump Sum Investment</h3>
                <p>Investing a large amount at once, typically when you have surplus funds available.</p>
                
                <h4>Advantages of Lump Sum:</h4>
                <ul>
                    <li>Higher returns in bull markets</li>
                    <li>Immediate full exposure to market growth</li>
                    <li>Lower transaction costs</li>
                    <li>Suitable for long-term goals</li>
                </ul>
                
                <h3>Which is Better?</h3>
                <p>The choice depends on your financial situation, market outlook, and investment goals. Many experts recommend a hybrid approach - combining both strategies for optimal results.</p>
            `
        },
        {
            id: 3,
            title: "TAX-SAVING MUTUAL FUNDS: MAXIMIZING ELSS BENEFITS",
            excerpt: "Discover how Equity Linked Savings Schemes (ELSS) can help you save taxes while building wealth through equity investments...",
            author: "AMIT KUMAR",
            readTime: "6 MIN READ",
            category: "TAX PLANNING",
            bgColor: "linear-gradient(135deg, #3182ce 0%, #2c5aa0 100%)",
            icon: <Shield size={40} />,
            content: `
                <h2>Understanding ELSS Mutual Funds</h2>
                <p>Equity Linked Savings Schemes (ELSS) are tax-saving mutual funds that offer the dual benefit of wealth creation and tax deduction under Section 80C of the Income Tax Act.</p>
                
                <h3>Key Features of ELSS:</h3>
                <ul>
                    <li><strong>Tax Deduction:</strong> Up to ₹1.5 lakh under Section 80C</li>
                    <li><strong>Lock-in Period:</strong> Shortest among 80C options at 3 years</li>
                    <li><strong>Equity Exposure:</strong> Minimum 80% in equity and equity-related instruments</li>
                    <li><strong>Growth Potential:</strong> Higher return potential compared to traditional tax-saving options</li>
                </ul>
                
                <h3>Benefits Over Other 80C Options:</h3>
                <table style="width: 100%; border-collapse: collapse; margin: 20px 0;">
                    <tr style="background-color: #f8f9fa;">
                        <th style="border: 1px solid #ddd; padding: 8px;">Investment Option</th>
                        <th style="border: 1px solid #ddd; padding: 8px;">Lock-in Period</th>
                        <th style="border: 1px solid #ddd; padding: 8px;">Expected Returns</th>
                    </tr>
                    <tr>
                        <td style="border: 1px solid #ddd; padding: 8px;">ELSS</td>
                        <td style="border: 1px solid #ddd; padding: 8px;">3 years</td>
                        <td style="border: 1px solid #ddd; padding: 8px;">12-15% p.a.</td>
                    </tr>
                    <tr>
                        <td style="border: 1px solid #ddd; padding: 8px;">PPF</td>
                        <td style="border: 1px solid #ddd; padding: 8px;">15 years</td>
                        <td style="border: 1px solid #ddd; padding: 8px;">7-8% p.a.</td>
                    </tr>
                    <tr>
                        <td style="border: 1px solid #ddd; padding: 8px;">NSC</td>
                        <td style="border: 1px solid #ddd; padding: 8px;">5 years</td>
                        <td style="border: 1px solid #ddd; padding: 8px;">6-7% p.a.</td>
                    </tr>
                </table>
                
                <h3>Who Should Invest in ELSS?</h3>
                <p>ELSS is ideal for investors looking to save taxes while participating in equity market growth. It's suitable for those with moderate to high risk tolerance and long-term investment horizon.</p>
            `
        }
    ];
    */

    // Load blogs on component mount
    useEffect(() => {
        fetchBlogs();
        fetchCategories();
    }, []);

    const openBlogDetail = (blog) => {
        setSelectedBlog(blog);
    };

    const closeBlogDetail = () => {
        setSelectedBlog(null);
    };

    // Loading state
    if (loading) {
        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: '#ffffff',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif',
                display: 'flex',
                flexDirection: 'column'
            }}>
                <Header />
                <div style={{
                    flex: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                }}>
                    <div style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        gap: '1rem'
                    }}>
                        <div style={{
                            width: '50px',
                            height: '50px',
                            border: '5px solid #f3f4f6',
                            borderTop: '5px solid #1a2332',
                            borderRadius: '50%',
                            animation: 'spin 1s linear infinite'
                        }} />
                        <p style={{ color: '#4a5568', fontSize: '1.1rem' }}>Loading blogs...</p>
                    </div>
                </div>
                <style>{`
                    @keyframes spin {
                        0% { transform: rotate(0deg); }
                        100% { transform: rotate(360deg); }
                    }
                `}</style>
            </div>
        );
    }

    // Error state with fallback
    if (error && blogPosts.length === 0) {
        return (
            <div style={{
                minHeight: '100vh',
                backgroundColor: '#ffffff',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}>
                <Header />
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '4rem 2rem',
                    textAlign: 'center'
                }}>
                    <div style={{
                        background: 'linear-gradient(135deg, #fee2e2 0%, #fecaca 100%)',
                        color: '#991b1b',
                        padding: '2rem',
                        borderRadius: '20px',
                        maxWidth: '600px',
                        margin: '0 auto'
                    }}>
                        <h2 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
                            Unable to Load Blogs
                        </h2>
                        <p style={{ marginBottom: '1.5rem' }}>{error}</p>
                        <button
                            onClick={fetchBlogs}
                            style={{
                                background: 'linear-gradient(135deg, #1a2332 0%, #2a4158 100%)',
                                color: 'white',
                                border: 'none',
                                padding: '0.75rem 1.5rem',
                                borderRadius: '25px',
                                fontSize: '1rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'transform 0.2s ease'
                            }}
                            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                        >
                            Try Again
                        </button>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    if (selectedBlog) {
        return (
            <>
                <style>{`
                    .back-button:hover {
                        transform: translateY(-2px) !important;
                        box-shadow: 0 8px 25px rgba(26, 35, 50, 0.3) !important;
                    }
                    
                    .blog-detail-content h2 {
                        color: #1a202c;
                        font-size: 1.8rem;
                        font-weight: 700;
                        margin: 2rem 0 1rem 0;
                        border-bottom: 3px solid #1a2332;
                        padding-bottom: 0.5rem;
                    }
                    
                    .blog-detail-content h3 {
                        color: #2a4158;
                        font-size: 1.4rem;
                        font-weight: 600;
                        margin: 1.5rem 0 1rem 0;
                    }
                    
                    .blog-detail-content h4 {
                        color: #1a202c;
                        font-size: 1.2rem;
                        font-weight: 600;
                        margin: 1.2rem 0 0.8rem 0;
                    }
                    
                    .blog-detail-content ul {
                        margin: 1rem 0;
                        padding-left: 2rem;
                    }
                    
                    .blog-detail-content li {
                        margin-bottom: 0.5rem;
                        line-height: 1.7;
                    }
                    
                    .blog-detail-content p {
                        margin: 1rem 0;
                        text-align: justify;
                    }
                    
                    .blog-detail-content table {
                        margin: 2rem 0;
                        border-radius: 8px;
                        overflow: hidden;
                        box-shadow: 0 4px 15px rgba(0,0,0,0.1);
                    }
                    
                    .blog-detail-content th {
                        background: linear-gradient(135deg, #1a2332 0%, #2a4158 100%) !important;
                        color: white !important;
                        font-weight: 600;
                        text-align: center;
                    }
                    
                    .blog-detail-content td {
                        text-align: center;
                        background-color: #f8f9fa;
                    }
                    
                    .blog-detail-content img {
                        max-width: 100%;
                        height: auto;
                        border-radius: 8px;
                        margin: 1rem 0;
                    }
                    
                    @media (max-width: 768px) {
                        .blog-detail-article {
                            padding: 2rem 1.5rem !important;
                        }
                        
                        .blog-detail-title {
                            font-size: 2rem !important;
                        }
                        
                        .blog-detail-content h2 {
                            font-size: 1.5rem !important;
                        }
                        
                        .blog-detail-content h3 {
                            font-size: 1.3rem !important;
                        }
                    }
                    
                    @media (max-width: 640px) {
                        .blog-detail-container {
                            padding: 0 1rem !important;
                        }
                        
                        .blog-detail-meta {
                            flex-direction: column !important;
                            align-items: flex-start !important;
                            gap: 0.5rem !important;
                        }
                    }
                `}</style>

                <div style={{
                    minHeight: '100vh',
                    backgroundColor: '#ffffff',
                    fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
                }}>
                    {/* Header */}
                    <Header />

                    {/* Blog Detail Content */}
                    <main style={{
                        padding: '4rem 2rem',
                        backgroundColor: '#f8fafc'
                    }}>
                        <div style={{
                            maxWidth: '900px',
                            margin: '0 auto'
                        }} className="blog-detail-container">
                            {/* Back Button */}
                            <button
                                onClick={closeBlogDetail}
                                style={{
                                    background: 'linear-gradient(135deg, #1a2332 0%, #2a4158 100%)',
                                    color: 'white',
                                    border: 'none',
                                    padding: '0.75rem 1.5rem',
                                    borderRadius: '25px',
                                    fontSize: '1rem',
                                    fontWeight: '600',
                                    cursor: 'pointer',
                                    marginBottom: '2rem',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '0.5rem',
                                    transition: 'all 0.3s ease',
                                    boxShadow: '0 4px 15px rgba(26, 35, 50, 0.3)'
                                }}
                                className="back-button"
                            >
                                ← Back to Blogs
                            </button>

                            {/* Blog Article */}
                            <article style={{
                                backgroundColor: 'white',
                                borderRadius: '20px',
                                padding: '3rem',
                                boxShadow: '0 20px 40px rgba(0,0,0,0.1)',
                                border: '1px solid #e2e8f0'
                            }} className="blog-detail-article">
                                {/* Article Header */}
                                <header style={{ 
                                    marginBottom: '3rem',
                                    paddingBottom: '2rem',
                                    borderBottom: '2px solid #f1f5f9'
                                }}>
                                    {/* Meta Information */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '1rem',
                                        marginBottom: '1.5rem',
                                        flexWrap: 'wrap'
                                    }} className="blog-detail-meta">
                                        <span style={{
                                            padding: '0.5rem 1rem',
                                            background: selectedBlog.bgColor,
                                            color: 'white',
                                            borderRadius: '20px',
                                            fontSize: '0.8rem',
                                            fontWeight: '700',
                                            letterSpacing: '0.5px'
                                        }}>
                                            {selectedBlog.category}
                                        </span>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            color: '#4a5568',
                                            fontSize: '0.9rem'
                                        }}>
                                            <User size={14} />
                                            {selectedBlog.author}
                                        </div>
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.5rem',
                                            color: '#4a5568',
                                            fontSize: '0.9rem'
                                        }}>
                                            <Clock size={14} />
                                            {selectedBlog.readTime}
                                        </div>
                                    </div>

                                    {/* Blog Title */}
                                    <h1 style={{
                                        fontSize: '2.8rem',
                                        fontWeight: '800',
                                        color: '#1a202c',
                                        lineHeight: '1.2',
                                        margin: '0',
                                        letterSpacing: '-0.5px'
                                    }} className="blog-detail-title">
                                        {selectedBlog.title}
                                    </h1>

                                    {/* Reading Progress Bar */}
                                    <div style={{
                                        width: '100%',
                                        height: '4px',
                                        backgroundColor: '#f1f5f9',
                                        borderRadius: '2px',
                                        marginTop: '1.5rem',
                                        overflow: 'hidden'
                                    }}>
                                        <div style={{
                                            width: '100%',
                                            height: '100%',
                                            background: selectedBlog.bgColor,
                                            borderRadius: '2px',
                                            animation: 'progressLoad 2s ease-in-out'
                                        }}></div>
                                    </div>
                                </header>

                                {/* Featured Image */}
                                {selectedBlog.featuredImageUrl && (
                                    <div style={{ marginBottom: '2rem' }}>
                                        <img 
                                            src={selectedBlog.featuredImageUrl} 
                                            alt={selectedBlog.title}
                                            style={{
                                                width: '100%',
                                                height: 'auto',
                                                borderRadius: '12px',
                                                boxShadow: '0 4px 15px rgba(0,0,0,0.1)'
                                            }}
                                        />
                                    </div>
                                )}

                                {/* Blog Content */}
                                <div 
                                    style={{
                                        fontSize: '1.1rem',
                                        lineHeight: '1.8',
                                        color: '#2d3748'
                                    }}
                                    className="blog-detail-content"
                                    dangerouslySetInnerHTML={{ __html: cleanBlogContent(selectedBlog.content) }}
                                />

                                {/* Tags */}
                                {selectedBlog.tags && selectedBlog.tags.length > 0 && (
                                    <div style={{
                                        marginTop: '2rem',
                                        paddingTop: '1rem',
                                        borderTop: '1px solid #e2e8f0'
                                    }}>
                                        <h4 style={{
                                            fontSize: '1rem',
                                            fontWeight: '600',
                                            color: '#4a5568',
                                            marginBottom: '0.5rem'
                                        }}>
                                            Tags:
                                        </h4>
                                        <div style={{
                                            display: 'flex',
                                            flexWrap: 'wrap',
                                            gap: '0.5rem'
                                        }}>
                                            {selectedBlog.tags.map((tag, index) => (
                                                <span
                                                    key={index}
                                                    style={{
                                                        background: '#f1f5f9',
                                                        color: '#4a5568',
                                                        padding: '0.25rem 0.75rem',
                                                        borderRadius: '12px',
                                                        fontSize: '0.8rem',
                                                        fontWeight: '500'
                                                    }}
                                                >
                                                    {tag}
                                                </span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Social Share Section */}
                                <footer style={{
                                    marginTop: '3rem',
                                    paddingTop: '2rem',
                                    borderTop: '2px solid #f1f5f9',
                                    textAlign: 'center'
                                }}>
                                    <h4 style={{
                                        fontSize: '1.2rem',
                                        fontWeight: '600',
                                        color: '#1a202c',
                                        marginBottom: '1rem'
                                    }}>
                                        Found this helpful? Share with others!
                                    </h4>
                                    <div style={{
                                        display: 'flex',
                                        gap: '1rem',
                                        justifyContent: 'center',
                                        flexWrap: 'wrap'
                                    }}>
                                        <button
                                            style={{
                                                background: 'linear-gradient(135deg, #3182ce 0%, #2c5aa0 100%)',
                                                color: 'white',
                                                border: 'none',
                                                padding: '0.75rem 1.5rem',
                                                borderRadius: '25px',
                                                fontSize: '0.9rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s ease'
                                            }}
                                            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                                            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                                            onClick={() => alert('Share on Twitter functionality coming soon!')}
                                        >
                                            Share on Twitter
                                        </button>
                                        <button
                                            style={{
                                                background: 'linear-gradient(135deg, #1a2332 0%, #2a4158 100%)',
                                                color: 'white',
                                                border: 'none',
                                                padding: '0.75rem 1.5rem',
                                                borderRadius: '25px',
                                                fontSize: '0.9rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s ease'
                                            }}
                                            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                                            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                                            onClick={() => alert('Share on Facebook functionality coming soon!')}
                                        >
                                            Share on Facebook
                                        </button>
                                        <button
                                            style={{
                                                background: 'linear-gradient(135deg, #4a5568 0%, #2d3748 100%)',
                                                color: 'white',
                                                border: 'none',
                                                padding: '0.75rem 1.5rem',
                                                borderRadius: '25px',
                                                fontSize: '0.9rem',
                                                fontWeight: '600',
                                                cursor: 'pointer',
                                                transition: 'transform 0.2s ease'
                                            }}
                                            onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                                            onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                                            onClick={() => alert('Share on LinkedIn functionality coming soon!')}
                                        >
                                            Share on LinkedIn
                                        </button>
                                    </div>
                                </footer>
                            </article>

                            {/* Related Posts Section */}
                            <section style={{
                                marginTop: '4rem',
                                textAlign: 'center'
                            }}>
                                <h3 style={{
                                    fontSize: '2rem',
                                    fontWeight: '700',
                                    color: '#1a202c',
                                    marginBottom: '2rem'
                                }}>
                                    Continue Reading
                                </h3>
                                <button
                                    onClick={closeBlogDetail}
                                    style={{
                                        background: 'linear-gradient(135deg, #2a4158 0%, #1a2332 100%)',
                                        color: 'white',
                                        border: 'none',
                                        padding: '1rem 2rem',
                                        borderRadius: '25px',
                                        fontSize: '1.1rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'all 0.3s ease',
                                        boxShadow: '0 4px 15px rgba(42, 65, 88, 0.3)'
                                    }}
                                    onMouseOver={(e) => {
                                        e.target.style.transform = 'translateY(-2px)';
                                        e.target.style.boxShadow = '0 8px 25px rgba(42, 65, 88, 0.4)';
                                    }}
                                    onMouseOut={(e) => {
                                        e.target.style.transform = 'translateY(0)';
                                        e.target.style.boxShadow = '0 4px 15px rgba(42, 65, 88, 0.3)';
                                    }}
                                >
                                    Explore More Investment Insights
                                </button>
                            </section>
                        </div>
                    </main>

                    {/* Layout */}
                    <Layout />

                    {/* Footer */}
                    <Footer />
                </div>
            </>
        );
    }

    return (
        <>
            <style>{`
                /* Hide all buttons in blog content that are image size selectors */
                .blog-detail-content button {
                    display: none !important;
                }
                
                /* Ensure images in blog content display properly */
                .blog-detail-content img {
                    max-width: 100%;
                    height: auto;
                    border-radius: 8px;
                    margin: 20px auto;
                    display: block;
                }
                
                .blog-detail-content .blog-image-wrapper {
                    margin: 20px 0;
                    text-align: center;
                }
                
                .blog-detail-content .blog-image-container {
                    display: inline-block;
                    max-width: 100%;
                }
                
                .form-input:focus, .form-textarea:focus {
                    outline: none;
                    border-color: #1a2332;
                    box-shadow: 0 0 0 3px rgba(26, 35, 50, 0.1);
                }
                
                @media (max-width: 1024px) {
                    .winnest-blog-title {
                        font-size: 1.8rem !important;
                    }
                    
                    .blog-grid {
                        grid-template-columns: repeat(auto-fit, minmax(320px, 1fr)) !important;
                    }
                }
                
                @media (max-width: 768px) {
                    .winnest-blog-title {
                        font-size: 1.5rem !important;
                    }
                    
                    .blog-grid {
                        grid-template-columns: 1fr !important;
                    }
                    
                    .blog-card {
                        margin: 0 1rem !important;
                    }
                }
                
                @media (max-width: 640px) {
                    .winnest-blog-title {
                        font-size: 1.3rem !important;
                    }
                }
            `}</style>

            <div style={{
                minHeight: '100vh',
                backgroundColor: '#ffffff',
                fontFamily: 'Inter, -apple-system, BlinkMacSystemFont, sans-serif'
            }}>
                {/* Header */}
                <Header />

                {/* Header Section - Matching OurTeam style */}
                <section style={{
                    background: '#1a2332',
                    backgroundImage: `
                        radial-gradient(circle at 25% 25%, rgba(255,255,255,0.1) 1px, transparent 1px),
                        radial-gradient(circle at 75% 75%, rgba(255,255,255,0.05) 1px, transparent 1px)
                    `,
                    backgroundSize: '20px 20px, 30px 30px',
                    padding: '2rem 0',
                    marginBottom: '0',
                    position: 'relative',
                    overflow: 'hidden',
                    borderBottom: '1px solid rgba(255,255,255,0.1)'
                }}>
                    {/* 3D Cubes */}
                    <div style={{
                        position: 'absolute',
                        top: '20px',
                        right: '10%',
                        transform: 'rotate(15deg)',
                        opacity: 0.8
                    }}>
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '10px'
                        }}>
                            <div style={{
                                display: 'flex',
                                gap: '10px'
                            }}>
                                <div style={{
                                    width: '60px',
                                    height: '60px',
                                    background: 'linear-gradient(45deg, #2a4158, #1a2332)',
                                    borderRadius: '8px',
                                    transform: 'rotateX(30deg) rotateY(-30deg)',
                                    boxShadow: '0 10px 20px rgba(0,0,0,0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '1.5rem'
                                }}>
                                    📈
                                </div>
                                <div style={{
                                    width: '60px',
                                    height: '60px',
                                    background: 'linear-gradient(45deg, #3182ce, #2c5aa0)',
                                    borderRadius: '8px',
                                    transform: 'rotateX(30deg) rotateY(-30deg)',
                                    boxShadow: '0 10px 20px rgba(0,0,0,0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '1.5rem'
                                }}>
                                    💰
                                </div>
                            </div>
                            <div style={{
                                display: 'flex',
                                gap: '10px',
                                marginLeft: '30px'
                            }}>
                                <div style={{
                                    width: '60px',
                                    height: '60px',
                                    background: 'linear-gradient(45deg, #4a5568, #2d3748)',
                                    borderRadius: '8px',
                                    transform: 'rotateX(30deg) rotateY(-30deg)',
                                    boxShadow: '0 10px 20px rgba(0,0,0,0.3)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    color: 'white',
                                    fontSize: '1.5rem'
                                }}>
                                    🎯
                                </div>
                            </div>
                        </div>
                    </div>

                    <div style={{
                        maxWidth: '1400px',
                        margin: '0 auto',
                        padding: '0 2rem',
                        position: 'relative',
                        zIndex: 2
                    }}>
                        <h1 style={{
                            fontSize: '2rem',
                            fontWeight: '600',
                            color: '#ffffff',
                            margin: '0',
                            textAlign: 'center',
                            textShadow: '0 1px 2px rgba(0,0,0,0.1)'
                        }} className="winnest-blog-title">
                            WINNEST BLOG
                        </h1>
                        <p style={{
                            fontSize: '1.1rem',
                            color: 'rgba(255,255,255,0.9)',
                            margin: '0.5rem 0 0 0',
                            fontWeight: '300',
                            textAlign: 'center'
                        }}>
                            Insights, strategies, and expert advice for mutual fund investing
                        </p>
                    </div>
                </section>

                {/* Error Message */}
                {error && (
                    <div style={{
                        padding: '1rem 2rem',
                        backgroundColor: '#fee2e2',
                        borderLeft: '4px solid #dc2626',
                        color: '#991b1b',
                        marginBottom: '1rem',
                        textAlign: 'center'
                    }}>
                        <p style={{ margin: '0', fontSize: '0.9rem' }}>
                            {error} (Showing cached content)
                        </p>
                    </div>
                )}

                {/* Blog Grid Section */}
                <section style={{
                    padding: '4rem 2rem',
                    maxWidth: '1200px',
                    margin: '0 auto'
                }}>
                    {/* Search and Filter Section */}
                    <div style={{
                        display: 'flex',
                        gap: '1rem',
                        marginBottom: '2rem',
                        flexWrap: 'wrap',
                        justifyContent: 'center',
                        alignItems: 'center'
                    }}>
                        {/* Search Input */}
                        <div style={{
                            position: 'relative',
                            flex: '1',
                            minWidth: '300px',
                            maxWidth: '400px'
                        }}>
                            <input
                                type="text"
                                placeholder="Search blogs..."
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                style={{
                                    width: '100%',
                                    padding: '0.75rem 1rem',
                                    border: '2px solid #e2e8f0',
                                    borderRadius: '25px',
                                    fontSize: '1rem',
                                    outline: 'none',
                                    transition: 'border-color 0.3s ease',
                                    background: 'white',
                                    boxSizing: 'border-box'
                                }}
                                onFocus={(e) => e.target.style.borderColor = '#1a2332'}
                                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
                            />
                        </div>

                        {/* Category Filter */}
                        <select
                            value={selectedCategory}
                            onChange={(e) => setSelectedCategory(e.target.value)}
                            style={{
                                padding: '0.75rem 1rem',
                                border: '2px solid #e2e8f0',
                                borderRadius: '25px',
                                fontSize: '1rem',
                                outline: 'none',
                                background: 'white',
                                cursor: 'pointer',
                                minWidth: '150px'
                            }}
                        >
                            <option value="">All Categories</option>
                            {categories.map(category => (
                                <option key={category} value={category}>{category}</option>
                            ))}
                        </select>

                        {/* Results Count */}
                        <div style={{
                            color: '#4a5568',
                            fontSize: '0.9rem',
                            fontWeight: '500'
                        }}>
                            {filteredBlogs.length} blog{filteredBlogs.length !== 1 ? 's' : ''} found
                        </div>

                        {/* Clear Filters */}
                        {(searchTerm || selectedCategory) && (
                            <button
                                onClick={() => {
                                    setSearchTerm('');
                                    setSelectedCategory('');
                                }}
                                style={{
                                    padding: '0.5rem 1rem',
                                    background: 'transparent',
                                    border: '1px solid #e2e8f0',
                                    borderRadius: '20px',
                                    fontSize: '0.9rem',
                                    cursor: 'pointer',
                                    color: '#4a5568',
                                    transition: 'all 0.2s ease'
                                }}
                                onMouseOver={(e) => {
                                    e.target.style.background = '#f8fafc';
                                    e.target.style.borderColor = '#1a2332';
                                }}
                                onMouseOut={(e) => {
                                    e.target.style.background = 'transparent';
                                    e.target.style.borderColor = '#e2e8f0';
                                }}
                            >
                                Clear Filters
                            </button>
                        )}
                    </div>

                    <div style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fit, minmax(380px, 1fr))',
                        gap: '2rem',
                        marginTop: '2rem'
                    }} className="blog-grid">
                        {filteredBlogs.map((blog) => (
                            <div
                                key={blog.id}
                                onClick={() => openBlogDetail(blog)}
                                style={{
                                    background: blog.bgColor,
                                    borderRadius: '20px',
                                    padding: '2.5rem',
                                    color: 'white',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease',
                                    position: 'relative',
                                    overflow: 'hidden',
                                    minHeight: '320px',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    justifyContent: 'space-between',
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
                                }}
                                className="blog-card"
                                onMouseOver={(e) => {
                                    e.currentTarget.style.transform = 'translateY(-10px)';
                                    e.currentTarget.style.boxShadow = '0 25px 50px rgba(0,0,0,0.2)';
                                }}
                                onMouseOut={(e) => {
                                    e.currentTarget.style.transform = 'translateY(0)';
                                    e.currentTarget.style.boxShadow = '0 10px 30px rgba(0,0,0,0.1)';
                                }}
                            >
                                {/* Background Pattern */}
                                <div style={{
                                    position: 'absolute',
                                    top: 0,
                                    left: 0,
                                    right: 0,
                                    bottom: 0,
                                    backgroundImage: `
                                        radial-gradient(circle at 80% 20%, rgba(255,255,255,0.1) 1px, transparent 1px),
                                        radial-gradient(circle at 20% 80%, rgba(255,255,255,0.05) 1px, transparent 1px)
                                    `,
                                    backgroundSize: '40px 40px, 60px 60px'
                                }} />

                                {/* Icon */}
                                <div style={{
                                    position: 'absolute',
                                    top: '1.5rem',
                                    right: '1.5rem',
                                    opacity: 0.3
                                }}>
                                    {blog.icon}
                                </div>

                                <div style={{ position: 'relative', zIndex: 2 }}>
                                    {/* Meta Info */}
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '1rem',
                                        marginBottom: '1.5rem',
                                        fontSize: '0.8rem',
                                        fontWeight: '600',
                                        letterSpacing: '0.5px'
                                    }}>
                                        <span style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.3rem'
                                        }}>
                                            <User size={12} />
                                            {blog.author}
                                        </span>
                                        <span>|</span>
                                        <span style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: '0.3rem'
                                        }}>
                                            <Clock size={12} />
                                            {blog.readTime}
                                        </span>
                                    </div>

                                    {/* Title */}
                                    <h2 style={{
                                        fontSize: '1.4rem',
                                        fontWeight: '800',
                                        lineHeight: '1.3',
                                        margin: '0 0 1rem 0',
                                        letterSpacing: '0.5px'
                                    }}>
                                        {blog.title}
                                    </h2>

                                    {/* Excerpt */}
                                    <p style={{
                                        fontSize: '0.95rem',
                                        lineHeight: '1.6',
                                        margin: '0 0 1.5rem 0',
                                        opacity: 0.9
                                    }}>
                                        {blog.excerpt}
                                    </p>
                                </div>

                                {/* Read More Button */}
                                <div style={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'space-between',
                                    position: 'relative',
                                    zIndex: 2
                                }}>
                                    <span style={{
                                        padding: '0.5rem 1rem',
                                        backgroundColor: 'rgba(255,255,255,0.2)',
                                        borderRadius: '15px',
                                        fontSize: '0.7rem',
                                        fontWeight: '700',
                                        letterSpacing: '0.5px'
                                    }}>
                                        {blog.category}
                                    </span>
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '0.5rem',
                                        fontSize: '0.9rem',
                                        fontWeight: '600',
                                        transition: 'gap 0.3s ease'
                                    }}
                                    onMouseOver={(e) => e.currentTarget.style.gap = '1rem'}
                                    onMouseOut={(e) => e.currentTarget.style.gap = '0.5rem'}
                                    >
                                        START READING
                                        <ArrowRight size={16} />
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>

                    {/* No blogs message */}
                    {filteredBlogs.length === 0 && !loading && (
                        <div style={{
                            textAlign: 'center',
                            padding: '4rem 2rem',
                            color: '#4a5568'
                        }}>
                            <h3 style={{ fontSize: '1.5rem', marginBottom: '1rem' }}>
                                {searchTerm || selectedCategory ? 'No blogs match your search' : 'No blogs available at the moment'}
                            </h3>
                            <p style={{ fontSize: '1rem', marginBottom: '2rem' }}>
                                {searchTerm || selectedCategory ? 'Try adjusting your search terms or filters.' : 'Check back later for fresh insights and investment strategies.'}
                            </p>
                            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', flexWrap: 'wrap' }}>
                                {(searchTerm || selectedCategory) && (
                                    <button
                                        onClick={() => {
                                            setSearchTerm('');
                                            setSelectedCategory('');
                                        }}
                                        style={{
                                            background: 'linear-gradient(135deg, #3182ce 0%, #2c5aa0 100%)',
                                            color: 'white',
                                            border: 'none',
                                            padding: '0.75rem 1.5rem',
                                            borderRadius: '25px',
                                            fontSize: '1rem',
                                            fontWeight: '600',
                                            cursor: 'pointer',
                                            transition: 'transform 0.2s ease'
                                        }}
                                        onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                                        onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                                    >
                                        Clear Filters
                                    </button>
                                )}
                                <button
                                    onClick={fetchBlogs}
                                    style={{
                                        background: 'linear-gradient(135deg, #1a2332 0%, #2a4158 100%)',
                                        color: 'white',
                                        border: 'none',
                                        padding: '0.75rem 1.5rem',
                                        borderRadius: '25px',
                                        fontSize: '1rem',
                                        fontWeight: '600',
                                        cursor: 'pointer',
                                        transition: 'transform 0.2s ease'
                                    }}
                                    onMouseOver={(e) => e.target.style.transform = 'translateY(-2px)'}
                                    onMouseOut={(e) => e.target.style.transform = 'translateY(0)'}
                                >
                                    Refresh Blogs
                                </button>
                            </div>
                        </div>
                    )}
                </section>

                {/* Layout */}
                <Layout />

                {/* Footer */}
                <Footer />
            </div>
        </>
    );
};

export default BlogPage;